import { useEffect, useRef, useState } from "react";
import { Save, Building2, Upload, ImageOff } from "lucide-react";
import { api, uploadFile, fileUrl } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Settings() {
  const { current, currentId, refreshCompanies } = useCompany();
  const [form, setForm] = useState({ name: "", address: "", gst_number: "", business_number: "", phone: "", email: "", website: "", contact_name: "", contact_phone: "", payment_terms: "", bank_name: "", bank_transit: "", bank_institution: "", bank_account: "", etransfer_email: "", invoice_template: "gold", filing_frequency: "quarterly", logo_path: "", default_timekeeper: "", default_rate: "", labour_cost_pct: "60" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (current) {
      setForm({
        name: current.name || "",
        address: current.address || "",
        gst_number: current.gst_number || "",
        business_number: current.business_number || "",
        phone: current.phone || "",
        email: current.email || "",
        website: current.website || "",
        contact_name: current.contact_name || "",
        contact_phone: current.contact_phone || "",
        payment_terms: current.payment_terms || "",
        bank_name: current.bank_name || "",
        bank_transit: current.bank_transit || "",
        bank_institution: current.bank_institution || "",
        bank_account: current.bank_account || "",
        etransfer_email: current.etransfer_email || "",
        invoice_template: current.invoice_template || "gold",
        filing_frequency: current.filing_frequency || "quarterly",
        logo_path: current.logo_path || "",
        default_timekeeper: current.default_timekeeper || "",
        default_rate: current.default_rate != null && current.default_rate !== 0 ? String(current.default_rate) : "",
        labour_cost_pct: current.labour_cost_pct != null ? String(current.labour_cost_pct) : "60",
      });
    }
  }, [current]);

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { path } = await uploadFile(file);
      await api.put(`/companies/${currentId}`, { logo_path: path });
      setForm((f) => ({ ...f, logo_path: path }));
      await refreshCompanies();
      toast.success("Logo updated — it will appear on invoice PDFs");
    } catch (err) {
      toast.error("Could not upload logo");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      await api.put(`/companies/${currentId}`, { ...form, default_rate: parseFloat(form.default_rate) || 0, labour_cost_pct: form.labour_cost_pct === "" ? 60 : parseFloat(form.labour_cost_pct) });
      await refreshCompanies();
      toast.success("Company profile saved");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Company Settings</h1>
        <p className="text-slate-500 text-sm mt-1">Profile & GST filing configuration for the active company.</p>
      </div>

      <Card className="p-6 rounded-lg border-slate-200 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-md bg-slate-900 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-heading font-semibold text-slate-900">{current?.name}</div>
            <Badge variant="outline" className="mt-0.5 capitalize bg-slate-50 text-slate-600 border-slate-200">
              {current?.role} · {current?.filing_frequency} filing
            </Badge>
          </div>
        </div>

        <div className="mb-6 flex items-center gap-4 rounded-lg border border-slate-200 p-4 bg-slate-50/60">
          <div className="h-20 w-20 rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
            {form.logo_path ? (
              <img src={fileUrl(form.logo_path)} alt="logo" className="max-h-full max-w-full object-contain" data-testid="settings-logo-preview" />
            ) : (
              <ImageOff className="h-6 w-6 text-slate-300" />
            )}
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-slate-900">Company Logo</div>
            <div className="text-xs text-slate-400 mb-2">PNG or JPG · shown on invoice PDFs.</div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onLogo} data-testid="settings-logo-input" />
            <Button variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="settings-logo-upload-btn" className="rounded-md">
              <Upload className="h-4 w-4 mr-2" /> {uploading ? "Uploading…" : form.logo_path ? "Replace logo" : "Upload logo"}
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label>Company Name</Label>
            <Input data-testid="settings-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-md" />
          </div>
          <div>
            <Label>Business Address</Label>
            <Textarea data-testid="settings-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 rounded-md" placeholder="Street, City, AB, Postal Code" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Phone</Label>
              <Input data-testid="settings-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-md" placeholder="(403) 555-0123" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="settings-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-md" placeholder="billing@company.com" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Website</Label>
              <Input data-testid="settings-website-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1 rounded-md" placeholder="company.com" />
            </div>
            <div>
              <Label>Invoice Contact Name</Label>
              <Input data-testid="settings-contact-name-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1 rounded-md" placeholder="Iain Munro" />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input data-testid="settings-contact-phone-input" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="mt-1 rounded-md" placeholder="403 555 0123" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>GST Number</Label>
              <Input data-testid="settings-gst-input" value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} className="mt-1 rounded-md" placeholder="123456789 RT0001" />
            </div>
            <div>
              <Label>Business Number</Label>
              <Input data-testid="settings-bn-input" value={form.business_number} onChange={(e) => setForm({ ...form, business_number: e.target.value })} className="mt-1 rounded-md" placeholder="123456789" />
            </div>
            <div>
              <Label>GST Filing Frequency</Label>
              <Select value={form.filing_frequency} onValueChange={(v) => setForm({ ...form, filing_frequency: v })}>
                <SelectTrigger data-testid="settings-frequency-select" className="mt-1 rounded-md capitalize">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-100 space-y-4">
          <div className="text-sm font-semibold text-slate-900">Invoice Payment Details</div>
          <div>
            <Label>Payment Terms</Label>
            <Input data-testid="settings-terms-input" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="mt-1 rounded-md" placeholder="Total payment due in 30 days" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label>Bank</Label>
              <Input data-testid="settings-bank-name-input" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="mt-1 rounded-md" placeholder="RBC" />
            </div>
            <div>
              <Label>Transit #</Label>
              <Input data-testid="settings-bank-transit-input" value={form.bank_transit} onChange={(e) => setForm({ ...form, bank_transit: e.target.value })} className="mt-1 rounded-md" placeholder="00836" />
            </div>
            <div>
              <Label>Institution #</Label>
              <Input data-testid="settings-bank-institution-input" value={form.bank_institution} onChange={(e) => setForm({ ...form, bank_institution: e.target.value })} className="mt-1 rounded-md" placeholder="003" />
            </div>
            <div>
              <Label>Account #</Label>
              <Input data-testid="settings-bank-account-input" value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} className="mt-1 rounded-md" placeholder="1038744" />
            </div>
          </div>
          <p className="text-xs text-slate-400">These appear in the "Other Comments" section of every invoice PDF for this company.</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>E-Transfer Email (optional)</Label>
              <Input data-testid="settings-etransfer-input" value={form.etransfer_email} onChange={(e) => setForm({ ...form, etransfer_email: e.target.value })} className="mt-1 rounded-md" placeholder="you@email.com" />
            </div>
            <div>
              <Label>Invoice Template</Label>
              <Select value={form.invoice_template} onValueChange={(v) => setForm({ ...form, invoice_template: v })}>
                <SelectTrigger data-testid="settings-template-select" className="mt-1 rounded-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="gold">Gold (Zentara style)</SelectItem>
                  <SelectItem value="blue">Blue (Abercalian style)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-100 space-y-4">
          <div className="text-sm font-semibold text-slate-900">Time / Labour Defaults</div>
          <p className="text-xs text-slate-400">Pre-fill new time entries for this company. You can still change them per entry.</p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Default Timekeeper</Label>
              <Input data-testid="settings-default-timekeeper-input" value={form.default_timekeeper} onChange={(e) => setForm({ ...form, default_timekeeper: e.target.value })} className="mt-1 rounded-md" placeholder="Iain Munro" />
            </div>
            <div>
              <Label>Default Rate ($/hr)</Label>
              <Input type="number" step="0.01" data-testid="settings-default-rate-input" value={form.default_rate} onChange={(e) => setForm({ ...form, default_rate: e.target.value })} className="mt-1 rounded-md text-right tabular-nums" placeholder="75" />
            </div>
            <div>
              <Label>Labour Cost (% of rate)</Label>
              <Input type="number" step="1" data-testid="settings-labour-cost-pct-input" value={form.labour_cost_pct} onChange={(e) => setForm({ ...form, labour_cost_pct: e.target.value })} className="mt-1 rounded-md text-right tabular-nums" placeholder="60" />
            </div>
          </div>
          <p className="text-xs text-slate-400">Job profitability treats labour cost as this % of the billable rate (invoices still bill the full rate).</p>
        </div>

        <div className="mt-6 flex justify-end">
          <Button data-testid="save-settings-btn" onClick={save} disabled={saving} className="rounded-md bg-slate-900 hover:bg-slate-800">
            <Save className="h-4 w-4 mr-2" /> Save Profile
          </Button>
        </div>
      </Card>
    </div>
  );
}
